import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { NavbarLogo } from "./Logo";
import {
  Menu,
  X,
  User,
  Calendar,
  Video,
  Building2,
  Stethoscope,
  LogOut,
  Settings,
  ChevronDown,
  Trash2,
} from "lucide-react";
import { Button } from "./ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { toast } from "sonner";
import { Alert } from "./ui/alert";
import { cn } from "./ui/utils";
import { useSelector } from "react-redux";

export const WebNavbar = () => {
  const { user, logout, clearAllData } = useAuth();
  // const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const profiledata = useSelector((state) => state?.cookies?.userDetails);
  const getDashboardRoute = (role) => {
    switch (role) {
      case "patient":
        return "/Dashboradpatient";
      case "agency":
        return "/dashboradScreen";
      case "doctor":
        return "/dashboard";
      default:
        return "/";
    }
  };

  const handleLogout = () => {
    console.log("🚪 WebNavbar: Logout button clicked");
    setMobileMenuOpen(false);

    toast.success("Logging out...", {
      duration: 2000,
    });
    logout();
  };

  const handleClearAllData = () => {
    console.log("🧹 WebNavbar: Clear all data button clicked");
    setMobileMenuOpen(false);

    if (
      Alert(
        "⚠️ This will clear ALL stored data and refresh the page. Continue?"
      )
    ) {
      toast.success("Clearing all data...", {
        duration: 2000,
      });

      clearAllData();

      // Force reload after a short delay
      setTimeout(() => {
        window.location.href = window.location.origin;
      }, 500);
    }
  };

  const isActive = (path) => location.pathname === path;

  const getDashboardPath = () => {
    if (!user) return "/";
    return `/${user.role}/dashboard`;
  };

  const getUserRoleDisplay = () => {
    switch (user?.role) {
      case "patient":
        return "Patient Portal";
      case "provider":
        return "Provider Portal";
      case "facility":
        return "Facility Portal";
      case "admin":
        return "Admin Portal";
      default:
        return "";
    }
  };

  return (
    <nav className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Enhanced Logo */}
          <NavbarLogo className="hover:scale-105 transition-transform duration-200" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {!user ? (
              <>
                <Link
                  to="/for-patients"

                  className={`
    text-gray-700 hover:text-blue-600 transition-colors font-medium no-underline
    ${isActive("/for-patients") ? "text-blue-600" : ""}
  `}
                >
                  For Patients
                </Link>
                <Link
                  to="/for-facilities"
                  className={`
    text-gray-700 hover:text-blue-600 transition-colors font-medium no-underline
    ${isActive("/for-facilities") ? "text-blue-600" : ""}
  `}
                >
                  For Facilities
                </Link>
                <Link
                  to="/providers"
                    className={`
    text-gray-700 hover:text-blue-600 transition-colors font-medium no-underline
    ${isActive("/providers") ? "text-blue-600" : ""}
  `}
                >
                  Find Providers
                </Link>
                <Link
                  to="/about"
                     className={`
    text-gray-700 hover:text-blue-600 transition-colors font-medium no-underline
    ${isActive("/about") ? "text-blue-600" : ""}
  `}
                >
                  About
                </Link>
                <Link
                  to="/contact"
                     className={`
    text-gray-700 hover:text-blue-600 transition-colors font-medium no-underline
    ${isActive("/contact") ? "text-blue-600" : ""}
  `}
                >
                  Contact
                </Link>
              </>
            ) : (
              <>
                <Link
                  to={getDashboardPath()}
                  className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${location.pathname.includes("/dashboard")
                      ? "text-blue-600"
                      : ""
                    }`}
                >
                  Dashboard
                </Link>
                {user.role === "patient" && (
                  <Link
                    to="/booking/telehealth"
                    className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${isActive("/booking/telehealth") ? "text-blue-600" : ""
                      }`}
                  >
                    Book Appointment
                  </Link>
                )}
                {user.role === "facility" && (
                  <Link
                    to="/booking/onsite"
                    className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${isActive("/booking/onsite") ? "text-blue-600" : ""
                      }`}
                  >
                    Request Visit
                  </Link>
                )}
                <Link
                  to="/providers"
                  className={`text-gray-700 hover:text-blue-600 transition-colors font-medium ${isActive("/providers") ? "text-blue-600" : ""
                    }`}
                >
                  Providers
                </Link>
              </>
            )}
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="flex items-center space-x-4">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    className="flex items-center space-x-2 h-auto p-2 hover:bg-clinic-blue-50 rounded-lg"
                  >
                    <div className="w-8 h-8 bg-gradient-to-br from-clinic-blue-100 to-clinic-blue-200 rounded-full flex items-center justify-center border border-clinic-blue-200">
                      <User className="h-4 w-4 text-clinic-blue-600" />
                    </div>
                    <div className="hidden sm:block text-left">
                      <div className="text-sm font-medium text-clinic-gray-900">
                        {user.name}
                      </div>
                      <div className="text-xs text-clinic-gray-500">
                        {getUserRoleDisplay()}
                      </div>
                    </div>
                    <ChevronDown className="h-4 w-4 text-clinic-gray-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem asChild>
                    <Link to={getDashboardPath()} className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      <span>Dashboard</span>
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "patient" && (
                    <>
                      <DropdownMenuItem asChild>
                        <Link
                          to="/booking/telehealth"
                          className="flex items-center"
                        >
                          <Video className="mr-2 h-4 w-4" />
                          <span>Book Telehealth</span>
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/providers" className="flex items-center">
                          <Stethoscope className="mr-2 h-4 w-4" />
                          <span>Find Providers</span>
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                  {user.role === "facility" && (
                    <DropdownMenuItem asChild>
                      <Link to="/booking/onsite" className="flex items-center">
                        <Building2 className="mr-2 h-4 w-4" />
                        <span>Request Visit</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  {user.role === "provider" && (
                    <DropdownMenuItem asChild>
                      <Link
                        to="/provider/schedule"
                        className="flex items-center"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        <span>My Schedule</span>
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem>
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    <span>Logout</span>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleClearAllData}
                    className="text-orange-600 focus:text-orange-600 focus:bg-orange-50"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    <span>Clear All Data (Debug)</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center space-x-3">
                {profiledata ? (
                  //  navigate("/Dashboradpatient");
                  <Link
                    to={getDashboardRoute(profiledata.role)}
                    className="text-clinic-gray-700 font-medium hover:text-clinic-blue-600 transition-colors duration-200"
                  >
                    {profiledata.firstName}
                  </Link>
                ) : (
                  <>
                    <Button
                      variant="ghost"
                      asChild
                      className="text-clinic-gray-700 hover:text-clinic-blue-600 hover:bg-clinic-blue-50"
                    >
                      <Link to="/login">Login</Link>
                    </Button>
                    <Button
                      asChild
                      className="bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white shadow-md hover:shadow-lg transition-all duration-200"
                    >
                      <Link to="/signup">Get Started</Link>
                    </Button>
                  </>
                )}
              </div>

            )}

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden text-clinic-gray-700 hover:text-clinic-blue-600 hover:bg-clinic-blue-50"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 bg-white">
            <div className="flex flex-col space-y-3">
              {!user ? (
                <>
                  <Link
                    to="/for-patients"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    For Patients
                  </Link>
                  <Link
                    to="/for-facilities"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    For Facilities
                  </Link>
                  <Link
                    to="/providers"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Find Providers
                  </Link>
                  <Link
                    to="/about"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    About
                  </Link>
                  <Link
                    to="/contact"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Contact
                  </Link>
                  <div className="flex flex-col space-y-2 px-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="ghost"
                      asChild
                      className="justify-start text-clinic-gray-700 hover:text-clinic-blue-600 hover:bg-clinic-blue-50"
                    >
                      <Link
                        to="/login"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                    </Button>
                    <Button
                      asChild
                      className="justify-start bg-clinic-blue-600 hover:bg-clinic-blue-700 text-white"
                    >
                      <Link
                        to="/signup"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Get{" "}
                      </Link>
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <div className="px-3 py-2 border-b border-gray-200 mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-clinic-blue-100 to-clinic-blue-200 rounded-full flex items-center justify-center border border-clinic-blue-200">
                        <User className="h-4 w-4 text-clinic-blue-600" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-clinic-gray-900">
                          {user.name}
                        </div>
                        <div className="text-xs text-clinic-gray-500">
                          {getUserRoleDisplay()}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Link
                    to={getDashboardPath()}
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  {user.role === "patient" && (
                    <Link
                      to="/booking/telehealth"
                      className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Book Appointment
                    </Link>
                  )}
                  {user.role === "facility" && (
                    <Link
                      to="/booking/onsite"
                      className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Request Visit
                    </Link>
                  )}
                  <Link
                    to="/providers"
                    className="text-clinic-gray-700 hover:text-clinic-blue-600 px-3 py-2 font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Providers
                  </Link>
                  <div className="flex flex-col space-y-2 px-3 pt-4 border-t border-gray-200">
                    <Button
                      variant="ghost"
                      onClick={handleLogout}
                      className="justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleClearAllData}
                      className="justify-start text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Clear All Data
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
